import React, { useState, useEffect } from 'react';
import './MassCommunication.css';
import AdminNav from '../Navbars/AdminNav';
import Footer from '../Footer';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../UserContext';
import { useContext } from 'react';
import { generateEmailTemplate } from './EmailTemplate';
import MarketingNav from '../Navbars/MarketingNav';

const MassCommunication = () => {
  const navigate = useNavigate();
  const { isAdmin, isTeacher, isStudent, isGuest, setUserRole, setUserInfo, userInfo } = useContext(UserContext);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [emailBatchId, setEmailBatchId] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [useTemplate, setUseTemplate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(true);

  const handleGroupSelect = (event) => {
    setSelectedGroup(event.target.value);
  };

  const handleFileUpload = (event) => {
    setMediaFile(event.target.files[0]);
  };

  const checkEmailStatus = async (batchId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_LOCAL_SERVER_API}/emailStatus/${batchId}`);
      const status = await response.json();

      setEmailStatus(status);

      if (status.processedCount < status.totalEmails) {
        setTimeout(() => checkEmailStatus(batchId), 5000);
      }
    } catch (error) {
      console.error('Error checking email status:', error);
    }
  };

  const handleUseTemplate = () => {
    setUseTemplate(!useTemplate);
    if (!useTemplate) {
      setEmailBody(generateEmailTemplate());
    }
  };

  const handleSendEmail = async () => {
    if (!selectedGroup) {
      alert('Please select a category');
      return;
    }

    if (!emailSubject) {
      alert('Please enter an email subject');
      return;
    }

    if (!emailBody) {
      alert('Please enter an email body');
      return;
    }

    try {
      setIsLoading(true);
      const div = document.createElement('div');
      div.innerHTML = emailBody;

      // Find all images in the content
      const images = div.getElementsByTagName('img');
      const attachments = [];

      // Define static images mapping
      const staticImages = {
        'cid:emailImage1': '../frontend/public/images/emailImage1.png',
        'cid:emailImage2': '../frontend/public/images/emailImage2.png',
        'cid:emailImage3': '../frontend/public/images/emailImage3.png',
        'cid:emailImage4': '../frontend/public/images/emailImage4.png',
        'cid:emailImage5': '../frontend/public/images/emailImage5.png',
        'cid:emailImage6': '../frontend/public/images/emailImage6.png',
        'cid:emailImage7': '../frontend/public/images/emailImage7.png',
        'cid:emailImage8': '../frontend/public/images/emailImage8.png',
        'cid:emailImage9': '../frontend/public/images/emailImage9.png',
        'cid:emailImage10': '../frontend/public/images/emailImage10.png',
        'cid:emailImage11': '../frontend/public/images/emailImage11.png',
        'cid:emailImage12': '../frontend/public/images/emailImage12.png',
        'cid:emailImage13': '../frontend/public/images/emailImage13.png',
        'cid:emailImage14': '../frontend/public/images/emailImage14.png',
        'cid:emailImage15': '../frontend/public/images/emailImage15.png'
      };

      Array.from(images).forEach((img, index) => {
        const cid = `emailImage${index + 1}`;
        const staticPath = staticImages[`cid:${cid}`];

        if (staticPath) {
          attachments.push({
            filename: `emailImage${index + 1}.png`,
            path: staticPath,
            cid: cid,
            contentType: 'image/png',
            contentDisposition: 'inline'
          });
        } else if (img.src.startsWith('data:image')) {
          const base64Data = img.src.split(',')[1];
          const mimeType = img.src.split(';')[0].split(':')[1];
          const extension = mimeType.split('/')[1];
          const filename = `inline-image-${index + 1}.${extension}`;

          attachments.push({
            filename: filename,
            content: base64Data,
            encoding: 'base64',
            cid: cid,
            contentType: mimeType,
            contentDisposition: 'inline'
          });
        }
      });

      const formData = new FormData();
      formData.append('selectedGroup', selectedGroup);
      formData.append('emailSubject', emailSubject);
      formData.append('emailBody', div.innerHTML);
      formData.append('inlineAttachments', JSON.stringify(attachments));

      if (mediaFile) {
        formData.append('mediaFile', mediaFile);
      }

      const response = await fetch(`${process.env.REACT_APP_LOCAL_SERVER_API}/sendMassEmail`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        alert(`Email queue created successfully. ${data.totalEmails} emails will be sent.`);
        setEmailBatchId(data.batchId);
        checkEmailStatus(data.batchId);

        // Reset form
        setSelectedGroup('');
        setEmailSubject('');
        setEmailBody('');
        setMediaFile(null);
      } else {
        alert(data.error || 'Failed to create email queue');
      }
    } catch (error) {
      console.error('Error creating email queue:', error);
      alert('Error creating email queue. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const onLogout = async () => {
    await fetch(`${process.env.REACT_APP_LOCAL_SERVER_API}/logout`, {
      credentials: 'include',
      method: 'POST',
    });
    navigate('/');
  };

  const renderEmailStatus = () => {
    if (!emailStatus) return null;

    const progress = Math.round((emailStatus.processedCount / emailStatus.totalEmails) * 100);

    return (
      <div className="email-status">
        <h3>Email Status</h3>
        <p>Progress: {progress}%</p>
        <p>Sent: {emailStatus.successCount}</p>
        <p>Failed: {emailStatus.errorCount}</p>
        <p>Total: {emailStatus.totalEmails}</p>
      </div>
    );
  };

  // Function to handle image paths for preview
  const getPreviewHtml = () => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = emailBody;

    // Replace CID references with actual image paths for preview
    const images = tempDiv.getElementsByTagName('img');
    Array.from(images).forEach((img) => {
      if (img.src.startsWith('cid:')) {
        const cid = img.src.replace('cid:', '');
        img.src = `/images/${cid}.png`;
      }
    });

    return tempDiv.innerHTML;
  };

  return (
    <>
      {userInfo?.usertype == "Admin" ? <AdminNav username={userInfo?.username} avatar={userInfo?.avatar} onLogout={onLogout} /> :
        <MarketingNav username={userInfo?.username} avatar={userInfo?.avatar} onLogout={onLogout} />
      }
      <div className="mass-communication-container">
        <h2>Mass Communication</h2>

        <div className="form-group">
          <label>Choose Category</label>
          <select value={selectedGroup} onChange={handleGroupSelect}>
            <option value="">Select Category</option>
            <option value="AllStudents">All Students</option>
            <option value="AllTeachers">All Teachers</option>
            <option value="ActivatedStudents">Activated Students</option>
            <option value="ActivatedTeachers">Activated Teachers</option>
            <option value="Polymatch">Polymatch</option>
            <option value="newsletter">News Letter</option>
          </select>
        </div>

        <div className="form-group">
          <label>Email Subject</label>
          <input
            type="text"
            placeholder="Enter email subject"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
          />
        </div>

        <div className="form-group">
          <div className="template-toggle">
            <label>
              <input
                type="checkbox"
                checked={useTemplate}
                onChange={handleUseTemplate}
              />
              Use Polymath Kids Email Template
            </label>
          </div>
        </div>

        <div className="form-group">
          <div className="editor-controls">
            <button
              className="preview-button"
              onClick={() => setShowPreview(!showPreview)}
            >
              {showPreview ? 'Edit HTML' : 'Preview Email'}
            </button>
          </div>
          <label>Email Body</label>
          {showPreview ? (
            <div
              className="email-preview"
              dangerouslySetInnerHTML={{ __html: getPreviewHtml() }}
            />
          ) : (
            <textarea
              className="html-editor"
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              placeholder="Enter HTML content"
            />
          )}
        </div>

        <div className="form-group">
          <label className="upload-label">Upload Media</label>
          <input type="file" onChange={handleFileUpload} />
        </div>

        <button
          className="send-button"
          onClick={handleSendEmail}
          disabled={isLoading}
        >
          {isLoading ? 'Preparing Email...' : 'Send Email'}
        </button>

        {renderEmailStatus()}
      </div>

      <Footer />
    </>
  );
};

export default MassCommunication;